/* Retailer map: a small static strip in the ERA map style.
   Same Mercator plane as the ERA community map (960x960, scale 960/2pi),
   simplified geography in itsfpv-retailer-geography.json. One dot per
   retailer; the camera frames the dots. Decorative only (aria-hidden):
   the logo tiles below carry the same names and links for every reader. */
(() => {
  'use strict';
  if (customElements.get('itsfpv-retailer-map')) return;

  const NS = 'http://www.w3.org/2000/svg';
  const SIZE = 960;
  const WORLD = [0, 30, 960, 690];
  const PAD = 70;
  const MIN_WIDTH = 300;
  const DOT_PX = 5;
  const cache = new Map();

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  function project(lat, lon) {
    const k = SIZE / (2 * Math.PI);
    const phi = clamp(lat, -85, 85) * Math.PI / 180;
    return [SIZE / 2 + k * lon * Math.PI / 180, SIZE / 2 - k * Math.log(Math.tan(Math.PI / 4 + phi / 2))];
  }

  function loadGeography(source) {
    const url = new URL(source, document.baseURI);
    const shopifyHost = ['cdn.shopify.com', 'cdn.shopifycdn.net'].includes(url.hostname);
    if (!['http:', 'https:'].includes(url.protocol) || (url.origin !== location.origin && !shopifyHost)) {
      return Promise.reject(new Error('Invalid source'));
    }
    if (!cache.has(url.href)) {
      cache.set(url.href, fetch(url.href, { credentials: 'omit' })
        .then((r) => { if (!r.ok) throw new Error('Unavailable'); return r.json(); })
        .then((geo) => {
          if (!geo || geo.width !== SIZE || !Array.isArray(geo.countries)) throw new Error('Invalid geography');
          return geo.countries.filter((c) => typeof c.code === 'string' && /^[A-Z]{2}$/.test(c.code)
            && typeof c.path === 'string' && !/[^MLZ0-9.,\- ]/.test(c.path));
        })
        .catch((e) => { cache.delete(url.href); throw e; }));
    }
    return cache.get(url.href);
  }

  function readRetailers(el) {
    try {
      const list = JSON.parse(el.querySelector('[data-retailers]')?.textContent || '[]');
      return list
        .map((r) => ({ ...r, lat: parseFloat(r.lat), lon: parseFloat(r.lon), country: String(r.country || '').toUpperCase() }))
        .filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lon) && Math.abs(r.lat) <= 85 && Math.abs(r.lon) <= 180);
    } catch { return []; }
  }

  class RetailerMap extends HTMLElement {
    connectedCallback() {
      this.retailers = readRetailers(this);
      if (!this.retailers.length) { this.hidden = true; return; }
      this.svg = this.querySelector('svg');
      loadGeography(this.dataset.geographyUrl).then((countries) => {
        if (!this.isConnected) return;
        this.render(countries);
        this.frame();
        this.observer = new ResizeObserver(() => this.frame());
        this.observer.observe(this);
        this.link();
        this.classList.add('is-ready');
      }).catch(() => { this.hidden = true; });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      (this.cleanups || []).forEach((fn) => fn());
    }

    render(countries) {
      const dealerCountries = new Set(this.retailers.map((r) => r.country));
      const land = countries.map((c) => {
        const p = document.createElementNS(NS, 'path');
        p.setAttribute('d', c.path);
        p.setAttribute('class', 'retailer-map__land' + (dealerCountries.has(c.code) ? ' retailer-map__land--dealer' : ''));
        return p;
      });
      this.querySelector('[data-land]').replaceChildren(...land);

      this.points = this.retailers.map((r) => project(r.lat, r.lon));
      this.dots = this.retailers.map((r, i) => {
        const a = document.createElementNS(NS, 'a');
        if (r.url) {
          a.setAttribute('href', r.url);
          a.setAttribute('target', '_blank');
          a.setAttribute('rel', 'noopener');
        }
        a.setAttribute('tabindex', '-1');
        const dot = document.createElementNS(NS, 'circle');
        dot.setAttribute('class', 'retailer-map__dot');
        dot.setAttribute('cx', this.points[i][0].toFixed(2));
        dot.setAttribute('cy', this.points[i][1].toFixed(2));
        const title = document.createElementNS(NS, 'title');
        title.textContent = r.label || r.name || '';
        dot.append(title);
        a.append(dot);
        a.dataset.retailerId = r.id;
        return a;
      });
      this.querySelector('[data-dots]').replaceChildren(...this.dots);
    }

    frame() {
      const box = this.getBoundingClientRect();
      if (!box.width || !box.height) return;
      const xs = this.points.map((p) => p[0]);
      const ys = this.points.map((p) => p[1]);
      let x = Math.min(...xs) - PAD;
      let y = Math.min(...ys) - PAD;
      let w = Math.max(Math.max(...xs) + PAD - x, MIN_WIDTH);
      let h = Math.max(...ys) + PAD - y;
      const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
      const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
      const aspect = box.width / box.height;
      if (w / h < aspect) w = h * aspect; else h = w / aspect;
      if (w > WORLD[2]) { w = WORLD[2]; h = w / aspect; }
      x = w >= WORLD[2] ? WORLD[0] : clamp(cx - w / 2, WORLD[0], WORLD[0] + WORLD[2] - w);
      y = h >= WORLD[3] ? WORLD[1] + (WORLD[3] - h) / 2 : clamp(cy - h / 2, WORLD[1], WORLD[1] + WORLD[3] - h);
      this.svg.setAttribute('viewBox', [x, y, w, h].map((v) => v.toFixed(2)).join(' '));
      const r = (DOT_PX * w / box.width).toFixed(3);
      this.dots.forEach((a) => a.firstChild.setAttribute('r', r));
    }

    link() {
      this.cleanups = [];
      const scope = this.closest('.retailer-logos') || document;
      const on = (el, type, fn) => { el.addEventListener(type, fn); this.cleanups.push(() => el.removeEventListener(type, fn)); };
      this.dots.forEach((dotLink) => {
        const tile = scope.querySelector(`[data-retailer-id="${CSS.escape(dotLink.dataset.retailerId)}"] .retailer-logos__tile`);
        if (!tile) return;
        const set = (active) => { dotLink.classList.toggle('is-active', active); tile.classList.toggle('is-active', active); };
        for (const [el, enter, leave] of [[tile, 'pointerenter', 'pointerleave'], [tile, 'focus', 'blur'], [dotLink, 'pointerenter', 'pointerleave']]) {
          on(el, enter, () => set(true));
          on(el, leave, () => set(false));
        }
      });
    }
  }

  customElements.define('itsfpv-retailer-map', RetailerMap);
})();
